import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { APP_NAME } from '../../config/appConstants';
import AuthPageFooter from '../../shared/components/common/AuthPageFooter';
import Logo from '../../shared/components/common/Logo';
import '../Login/Login.css';
import './ResetPassword.css';
import appLogo from '../../assets/images/DevicePulse-app-logo2.png';

/**
 * PasswordResetRequired Component
 *
 * Phase 1: Forced password reset on first login
 *
 * This page is shown to users with password_reset_required = true
 * Users MUST set their own password before accessing the application
 *
 * Flow:
 * 1. User receives temporary password via email (after email verification)
 * 2. User logs in with temporary password
 * 3. Redirected to this page
 * 4. User sets their own password (min 8 characters)
 * 5. password_reset_required flag is cleared
 * 6. User is redirected to dashboard
 */
function PasswordResetRequired({ user: propUser }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const navigate = useNavigate();

  // Use passed props (provided by App.js)
  const user = propUser;

  // Redirect if user is not authenticated or doesn't need password reset
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (user.password_reset_required === false) {
      // User already has password set, redirect to dashboard
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

    return Math.min(strength, 5);
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Call change password endpoint
      const token = sessionStorage.getItem('token');

      // For first-time password set, use the new password as current password
      // (user is logging in with temp password, so current password = temp password)
      // But since we don't have the temp password here, we send both passwords
      // Backend will handle this special case for password_reset_required users
      await axios.post(
        API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        {
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Clear form fields and show success state
      setNewPassword('');
      setConfirmPassword('');

      // Update user state in sessionStorage to reflect that password_reset_required is now false
      // This prevents App.js from redirecting back to this page when user navigates to login
      const userFromStorage = sessionStorage.getItem('user');
      if (userFromStorage) {
        const updatedUser = JSON.parse(userFromStorage);
        updatedUser.password_reset_required = false;
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
      }

      setIsSuccessful(true);

    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null; // Will redirect via useEffect
  }

  const getPasswordStrengthLabel = (strength) => {
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    return labels[strength] || '';
  };

  const getPasswordStrengthColor = (strength) => {
    const colors = ['#d32f2f', '#f57c00', '#fbc02d', '#7cb342', '#388e3c', '#1b5e20'];
    return colors[strength] || '#999';
  };

  // Show success confirmation view after password is reset
  if (isSuccessful) {
    const handleGoToLogin = () => {
      // Clear all authentication state before navigating to login
      // This prevents App.js from redirecting back to password reset form
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('location_id');
      sessionStorage.removeItem('location_name');
      sessionStorage.removeItem('organization_name');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('session_created_at');
      sessionStorage.removeItem('browser_mode');

      // Use window.location.href to perform a hard redirect
      // This forces a full page reload, clearing App.js state and reloading from sessionStorage
      window.location.href = '/login';
    };

    return (
      <div className="login-container">
        {/* Left Side - Animated Gradient Background */}
        <div className="login-left-side">
          {/* Background Layers */}
          <div className="particles"></div>
          <div className="medical-pattern"></div>

          {/* Floating Medical Device Illustrations */}
          <div className="medical-float-1">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
              <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
              <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
              <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
              <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
            </svg>
          </div>

          <div className="medical-float-2">
            <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
              <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
              <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
              <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
              <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
            </svg>
          </div>

          <div className="medical-float-3">
            <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="20" y="30" width="60" height="70" rx="8" fill="white" opacity="0.9"/>
              <rect x="30" y="45" width="40" height="25" rx="3" fill="rgba(102,126,234,0.3)"/>
              <circle cx="35" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
              <circle cx="65" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
              <rect x="40" y="20" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
              <line x1="30" y1="55" x2="70" y2="55" stroke="white" strokeWidth="2"/>
            </svg>
          </div>

          <div className="medical-float-4">
            <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="55" cy="35" r="20" fill="white" opacity="0.9"/>
              <rect x="50" y="50" width="10" height="40" fill="white" opacity="0.9"/>
              <circle cx="55" cy="35" r="12" fill="rgba(102,126,234,0.3)"/>
              <path d="M 45,30 L 55,25 L 65,30" stroke="white" strokeWidth="2" fill="none"/>
              <rect x="35" y="85" width="40" height="15" rx="5" fill="white" opacity="0.8"/>
            </svg>
          </div>

          <div className="medical-float-5">
            <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="15" y="15" width="90" height="60" rx="5" fill="white" opacity="0.9"/>
              <rect x="20" y="20" width="80" height="50" rx="3" fill="rgba(102,126,234,0.3)"/>
              <path d="M 30,45 L 45,45 L 50,35 L 55,55 L 60,40 L 70,45 L 90,45" stroke="rgba(102,126,234,0.6)" strokeWidth="3" fill="none"/>
              <rect x="45" y="75" width="30" height="5" fill="white" opacity="0.8"/>
              <rect x="35" y="80" width="50" height="15" rx="3" fill="white" opacity="0.9"/>
            </svg>
          </div>

          <div className="medical-float-6">
            <svg width="100" height="110" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="25" y="25" width="50" height="60" rx="6" fill="white" opacity="0.9"/>
              <rect x="35" y="35" width="30" height="20" rx="3" fill="rgba(102,126,234,0.3)"/>
              <circle cx="40" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
              <circle cx="60" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
              <rect x="40" y="85" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
            </svg>
          </div>

          {/* Left Side Content */}
          <div className="left-side-content">
            <div className="left-side-text">
              <h2>Welcome to</h2>
              <h1>
                <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
                {APP_NAME}
              </h1>
              <p>Your password has been successfully set. You're ready to access your account.</p>
            </div>
          </div>
        
        </div>

        {/* Right Side - Success Message */}
        <div className="login-right-side">
          <AuthPageFooter />
          <div className="login-box">
            <div className="login-header">
              <h3>Password Set Successfully!</h3>
              <p>You can now log in with your new password</p>
            </div>

            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '72px', marginBottom: '24px' }}>✅</div>
              <p style={{ fontSize: '16px', color: '#555', marginBottom: '30px', lineHeight: '1.6' }}>
                Your password has been updated successfully. You are all set to access the application with your new credentials.
              </p>
            </div>

            <button
              type="button"
              className="action-btn action-primary action-btn-block"
              onClick={handleGoToLogin}
            >
              Go to Login
            </button>

            <div style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px',
              border: '1px solid #81c784',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0', fontSize: '0.85rem', color: '#2e7d32', lineHeight: '1.4' }}>
                You'll be logged out and need to log in again with your new password.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left Side - Animated Gradient Background */}
      <div className="login-left-side">
        {/* Background Layers */}
        <div className="particles"></div>
        <div className="medical-pattern"></div>

        {/* Floating Medical Device Illustrations */}
        <div className="medical-float-1">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
            <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
            <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
            <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
            <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-2">
          <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
            <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
            <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
          </svg>
        </div>

        <div className="medical-float-3">
          <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="30" width="60" height="70" rx="8" fill="white" opacity="0.9"/>
            <rect x="30" y="45" width="40" height="25" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="35" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <circle cx="65" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="20" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
            <line x1="30" y1="55" x2="70" y2="55" stroke="white" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-4">
          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="55" cy="35" r="20" fill="white" opacity="0.9"/>
            <rect x="50" y="50" width="10" height="40" fill="white" opacity="0.9"/>
            <circle cx="55" cy="35" r="12" fill="rgba(102,126,234,0.3)"/>
            <path d="M 45,30 L 55,25 L 65,30" stroke="white" strokeWidth="2" fill="none"/>
            <rect x="35" y="85" width="40" height="15" rx="5" fill="white" opacity="0.8"/>
          </svg>
        </div>

        <div className="medical-float-5">
          <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="90" height="60" rx="5" fill="white" opacity="0.9"/>
            <rect x="20" y="20" width="80" height="50" rx="3" fill="rgba(102,126,234,0.3)"/>
            <path d="M 30,45 L 45,45 L 50,35 L 55,55 L 60,40 L 70,45 L 90,45" stroke="rgba(102,126,234,0.6)" strokeWidth="3" fill="none"/>
            <rect x="45" y="75" width="30" height="5" fill="white" opacity="0.8"/>
            <rect x="35" y="80" width="50" height="15" rx="3" fill="white" opacity="0.9"/>
          </svg>
        </div>

        <div className="medical-float-6">
          <svg width="100" height="110" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="25" width="50" height="60" rx="6" fill="white" opacity="0.9"/>
            <rect x="35" y="35" width="30" height="20" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="40" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <circle cx="60" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="85" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
          </svg>
        </div>

        {/* Left Side Content */}
        <div className="left-side-content">
          <div className="left-side-text">
            <h2>Welcome to</h2>
            <h1>
              <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
              {APP_NAME}
            </h1>
            <p>Create a strong password to secure your account and get started</p>
          </div>
        </div>

      </div>

      {/* Right Side - Reset Password Form */}
      <div className="login-right-side">
        <AuthPageFooter />

        <div className="login-box">
          <div className="login-header">
            <h3>Set Your Password</h3>
            <p>Create a secure password to protect your account</p>
          </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword">New Password *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter a strong password"
                required
                minLength="8"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '4px 8px'
                }}
                disabled={loading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            {newPassword && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
                  {[0, 1, 2, 3, 4].map((index) => (
                    <div
                      key={index}
                      style={{
                        height: '4px',
                        flex: 1,
                        backgroundColor: index < passwordStrength
                          ? getPasswordStrengthColor(passwordStrength - 1)
                          : '#e0e0e0',
                        borderRadius: '2px'
                      }}
                    />
                  ))}
                </div>
                <small style={{ color: getPasswordStrengthColor(passwordStrength), fontWeight: '600' }}>
                  {getPasswordStrengthLabel(passwordStrength)}
                </small>
              </div>
            )}

            <small className="help-text" style={{ marginTop: '8px', display: 'block' }}>
              ✓ Must be at least 8 characters<br/>
              ✓ Mix of uppercase, lowercase, numbers, and symbols recommended
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              minLength="8"
              disabled={loading}
            />
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <small style={{ color: '#d32f2f', display: 'block', marginTop: '4px' }}>
                ⚠️ Passwords do not match
              </small>
            )}
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <small style={{ color: '#27ae60', display: 'block', marginTop: '4px' }}>
                ✓ Passwords match
              </small>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="action-btn action-primary action-btn-block"
            disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            {loading ? 'Setting Password...' : 'Set Password & Continue'}
          </button>

          <div className="back-to-login">
            <button
              type="button"
              onClick={() => {
                // Clear authentication state to prevent redirect loop
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('user');
                sessionStorage.removeItem('location_id');
                sessionStorage.removeItem('location_name');
                sessionStorage.removeItem('organization_name');
                sessionStorage.removeItem('session_id');
                sessionStorage.removeItem('session_created_at');
                sessionStorage.removeItem('browser_mode');
                // Use window.location.href to perform a hard redirect
                // This forces a full page reload, clearing App.js state
                window.location.href = '/login';
              }}
            >
              Back to Login
            </button>
          </div>
        </form>

          <div style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px',
            border: '1px solid #90caf9'
          }}>
            <p style={{ margin: '0', fontSize: '0.85rem', color: '#1565c0', lineHeight: '1.4' }}>
              <strong>🔐 Security Requirement</strong><br/>
              Setting a new password is required to protect your account and ensure secure access to the system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetRequired;
